import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { Edit, Delete, Add, CloudUpload } from '@mui/icons-material';
import { getBanners, createBanner, updateBanner, deleteBanner } from '../../services/bannersApi';

const defaultForm = {
  type: '',
  template: '',
  priority: '',
  expiry: '',
  click_tracking_id: '',
  title: '',
  subtitle: '',
  cta_url: '',
  cta_text: '',
  color_1: '',
  color_2: '',
  image: null,
};

function buildFormData(data) {
  const formData = new FormData();
  if (data.type) formData.append('type', data.type);
  if (data.template) formData.append('template', data.template);
  if (data.priority !== '' && data.priority !== undefined) formData.append('priority', data.priority);
  if (data.expiry) formData.append('expiry', data.expiry);
  if (data.click_tracking_id) formData.append('click_tracking_id', data.click_tracking_id);
  if (data.title) formData.append('title', data.title);
  if (data.subtitle) formData.append('subtitle', data.subtitle);
  if (data.cta_url) formData.append('cta_url', data.cta_url);
  if (data.cta_text) formData.append('cta_text', data.cta_text);
  if (data.color_1) formData.append('color_1', data.color_1);
  if (data.color_2) formData.append('color_2', data.color_2);
  if (data.image) formData.append('image', data.image);
  return formData;
}

function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await getBanners(100, 0);
      if (response.statusCode === '10000') {
        setBanners(response.data?.banners || response.data || []);
      } else if (response.statusCode === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => { window.location = '/login'; }, 2000);
      } else {
        showSnackbar(response.message || 'Error fetching banners', 'error');
        setBanners([]);
      }
    } catch (err) {
      showSnackbar('Error fetching banners', 'error');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      const meta = banner.metadata || {};
      const cta = meta.cta || {};
      setFormData({
        type: banner.type || '',
        template: banner.template || '',
        priority: banner.priority ?? '',
        expiry: banner.expiry ? banner.expiry.slice(0, 16) : '',
        click_tracking_id: banner.click_tracking_id || '',
        title: meta.title || '',
        subtitle: meta.subtitle || '',
        cta_url: cta.cta_url || '',
        cta_text: cta.cta_text || '',
        color_1: meta.color_1 || '',
        color_2: meta.color_2 || '',
        image: null,
      });
    } else {
      setEditingBanner(null);
      setFormData({ ...defaultForm });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBanner(null);
    setFormData({ ...defaultForm });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = buildFormData(formData);
      const response = editingBanner ? await updateBanner(editingBanner.id, fd) : await createBanner(fd);
      if (response.statusCode === '10000') {
        showSnackbar(editingBanner ? 'Banner updated successfully' : 'Banner created successfully', 'success');
        handleCloseDialog();
        fetchBanners();
      } else if (response.statusCode === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => { window.location = '/login'; }, 2000);
      } else {
        showSnackbar(response.message || 'Error saving banner', 'error');
      }
    } catch (err) {
      showSnackbar('Error saving banner', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    setLoading(true);
    try {
      const response = await deleteBanner(id);
      if (response.statusCode === '10000') {
        showSnackbar('Banner deleted successfully', 'success');
        fetchBanners();
      } else if (response.statusCode === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => { window.location = '/login'; }, 2000);
      } else {
        showSnackbar(response.message || 'Error deleting banner', 'error');
      }
    } catch (err) {
      showSnackbar('Error deleting banner', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const imageUrl = (banner) => {
    const meta = banner.metadata || {};
    return meta.image_url || '';
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Banners</h3>
                </div>
                <div className="col-sm-6">
                  <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ float: 'right' }}>
                    Add Banner
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    {loading && !banners.length ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>ID</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Template</strong></TableCell>
                              <TableCell><strong>Priority</strong></TableCell>
                              <TableCell><strong>Image</strong></TableCell>
                              <TableCell><strong>Title</strong></TableCell>
                              <TableCell><strong>Expiry</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!banners.length ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Typography variant="body1" color="text.secondary">No banners found</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              banners.map((b) => (
                                <TableRow key={b.id}>
                                  <TableCell>{b.id}</TableCell>
                                  <TableCell>{b.type || '-'}</TableCell>
                                  <TableCell>{b.template || '-'}</TableCell>
                                  <TableCell>{b.priority ?? '-'}</TableCell>
                                  <TableCell>
                                    {imageUrl(b) ? (
                                      <img src={imageUrl(b)} alt="" style={{ width: 50, height: 50, objectFit: 'cover' }} />
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell>{(b.metadata && b.metadata.title) || '-'}</TableCell>
                                  <TableCell>{b.expiry ? new Date(b.expiry).toLocaleString() : '-'}</TableCell>
                                  <TableCell>
                                    <IconButton color="primary" size="small" onClick={() => handleOpenDialog(b)}><Edit /></IconButton>
                                    <IconButton color="error" size="small" onClick={() => handleDelete(b.id)}><Delete /></IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingBanner ? 'Edit Banner' : 'Add Banner'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="type" label="Type" value={formData.type} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="template" label="Template" value={formData.template} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="priority" label="Priority" type="number" value={formData.priority} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="expiry" label="Expiry" type="datetime-local" value={formData.expiry} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth name="click_tracking_id" label="Click Tracking ID" value={formData.click_tracking_id} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth name="title" label="Title" value={formData.title} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth name="subtitle" label="Subtitle" value={formData.subtitle} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="cta_url" label="CTA URL" value={formData.cta_url} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="cta_text" label="CTA Text" value={formData.cta_text} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="color_1" label="Color 1" value={formData.color_1} onChange={handleInputChange} placeholder="#hex or name" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="color_2" label="Color 2" value={formData.color_2} onChange={handleInputChange} placeholder="#hex or name" />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth sx={{ py: 2 }}>
                    {formData.image ? formData.image.name : 'Upload Image (optional)'}
                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                  </Button>
                  {editingBanner && imageUrl(editingBanner) && !formData.image && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Current image:</Typography>
                      <img src={imageUrl(editingBanner)} alt="Current" style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }} />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} /> : (editingBanner ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default Banners;
